/**
 * useEC2Session — manages the backend session lifecycle.
 *
 * Supports two modes:
 *   1. Legacy: per-user EC2 instance via Lambda proxy (45s boot wait)
 *   2. New: shared GPU service via ALB (no boot wait, warm pool ~10-15s)
 *
 * Mode is determined by REACT_APP_BACKEND_URL env var:
 *   - Set: uses ALB-based shared backend (new)
 *   - Not set: falls back to legacy EC2 proxy
 *
 * Returns state + helpers consumed by Segmentationbeta.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSnackbar } from 'notistack';

const LEGACY_BACKEND_URL =
  'https://a0kv32c0yj.execute-api.us-east-1.amazonaws.com/proxy';
const TERMINATE_URL =
  'https://0695pycn1k.execute-api.us-east-1.amazonaws.com/prod/instances/terminate/';

const isNewBackend = !!process.env.REACT_APP_BACKEND_URL;

// How long after the last user interaction a tab keeps heartbeating. Past this
// (or while the tab is hidden) the heartbeat stops, the server-side session
// lapses in ~5 min and frees its slot. Exported for deterministic testing.
export const HEARTBEAT_IDLE_MS = 10 * 60 * 1000;

/**
 * Whether an open analysis tab should send a keep-alive heartbeat. Pure so the
 * slot-leak gate is testable without rendering the hook. Skip when the tab is
 * hidden (backgrounded) or the user has been idle longer than idleMs.
 */
export function shouldHeartbeat({ visibilityState, lastActivity, now, idleMs = HEARTBEAT_IDLE_MS }) {
  if (visibilityState === 'hidden') return false;
  return now - lastActivity <= idleMs;
}

export default function useEC2Session({
  initialPath,
  sessionIdQuery,
  analysisRestoreMode,
  backendUrl: backendUrlProp,
  singleImageMode = false,
}) {
  const { enqueueSnackbar } = useSnackbar();

  // Resolve backend URL: prop > GPU override (RunPod proxy) > env var > legacy.
  // REACT_APP_GPU_URL points GPU traffic at an alternate host (e.g. a RunPod pod
  // proxy URL that serves FastAPI at root). When unset, behavior is unchanged.
  const backendUrl = backendUrlProp
    || process.env.REACT_APP_GPU_URL
    || (isNewBackend ? `${process.env.REACT_APP_BACKEND_URL}/gpu` : LEGACY_BACKEND_URL);

  // ── public state ──
  const [settingUpResources, setSettingUpResources] = useState(true);
  const [serverReady, setServerReady] = useState(false);
  const [sessionCreated, setSessionCreated] = useState(false);
  const [sessionProgress, setSessionProgress] = useState({
    stage: '',
    progress: 0,
    message: '',
  });
  const [isEnding, setIsEnding] = useState(false);
  // Terminal setup error with the real backend reason (e.g. "No images found
  // for this experiment"), so the page can show the actual cause instead of a
  // misleading generic "Connection Failed / server could not be reached".
  const [sessionError, setSessionError] = useState(null);
  // Reactive session ID: initialized from URL param, updated once the backend
  // confirms (or reuses) the session. Consumers should prefer this over reading
  // sessionStorage directly so they react to changes without stale snapshots.
  const [resolvedSessionId, setResolvedSessionId] = useState(sessionIdQuery || null);

  // ── internal refs ──
  const healthCheckStarted = useRef(false);
  const sessionCreatedRef = useRef(false);
  const heartbeatTimer = useRef(null);
  const lastActivity = useRef(Date.now());
  const initRetryCount = useRef(0);
  const MAX_INIT_RETRIES = 2;
  // Hard cap on automatic session re-creation (e.g. after a backend restart the
  // tab sees a 404 and rebuilds its session). Without a cap, a forgotten/stuck
  // tab silently re-creates sessions forever and refills GPU slots, starving
  // everyone. After this many auto-recreates the tab stops and asks the user to
  // reload manually. Counts for the lifetime of the tab, never reset.
  const recreateCount = useRef(0);
  const MAX_RECREATES = 2;

  useEffect(() => {
    sessionCreatedRef.current = sessionCreated;
  }, [sessionCreated]);

  // ── helpers ──

  const waitForInferenceReady = useCallback(
    async (sessionId, maxWaitTime = 60000) => {
      const startTime = Date.now();
      let consecutiveErrors = 0;
      while (Date.now() - startTime < maxWaitTime) {
        try {
          const response = await fetch(
            `${backendUrl}/check_inference_ready/${sessionId}?sessionId=${encodeURIComponent(sessionId)}`
          );
          if (response.status === 404) {
            // Session no longer exists (ECS rollover). Clear stale state and
            // let the caller or boot sequence recreate it.
            console.warn('Session', sessionId, 'not found (404). Clearing stale session.');
            sessionStorage.removeItem('sessionId');
            sessionStorage.removeItem('ec2Instance');
            setSessionCreated(false);
            setSettingUpResources(false);
            enqueueSnackbar(
              'Connection lost. Please refresh to start over.',
              { variant: 'warning' }
            );
            return;
          }
          consecutiveErrors = 0;
          const result = await response.json();
          setSessionProgress((prev) => ({
            ...prev,
            stage: 'initializing_inference',
            progress: 95,
            message: result.message || 'Preparing analysis...',
          }));
          if (result.inference_ready) {
            setServerReady(true);
            setSettingUpResources(false);
            enqueueSnackbar('Ready to analyze!', { variant: 'success' });
            return;
          }
          await new Promise((r) => setTimeout(r, 3000));
        } catch {
          consecutiveErrors++;
          // Be patient with transient blips: a single RunPod pod under load can
          // briefly 5xx/CORS-fail health checks while a session initializes.
          // Bailing at 15s surfaced false "analysis server could not be reached"
          // errors. Honor the full maxWaitTime and a higher consecutive-error
          // budget before declaring the session stale.
          if (consecutiveErrors >= 10 || Date.now() - startTime > maxWaitTime) {
            // Too many consecutive errors — session is likely stale.
            sessionStorage.removeItem('sessionId');
            sessionStorage.removeItem('ec2Instance');
            setSessionCreated(false);
            setSettingUpResources(false);
            enqueueSnackbar(
              'Connection lost. Please refresh to try again.',
              { variant: 'warning' }
            );
            return;
          }
          await new Promise((r) => setTimeout(r, 3000));
        }
      }
      // Timed out waiting for inference_ready — clear stale session so next
      // reload starts fresh instead of reusing this broken session id.
      sessionStorage.removeItem('sessionId');
      sessionStorage.removeItem('ec2Instance');
      setSessionCreated(false);
      setSettingUpResources(false);
      enqueueSnackbar(
        'Could not connect to the analysis server. Please reload to try again.',
        { variant: 'error' }
      );
    },
    [backendUrl, enqueueSnackbar]
  );

  const pollSessionProgress = useCallback(
    (taskId, sessionId, onNotFound, onError) => {
      if (!taskId) return;

      // Sequential polling: each iteration waits for the previous fetch to
      // complete before scheduling the next.  This prevents the setInterval
      // concurrent-fetch pile-up where 30+ in-flight requests all hit a
      // crashing container simultaneously and each independently clear state.
      //
      // Retry budget: up to 15 consecutive network/HTTP errors (~30s) before
      // giving up.  Each fetch is also capped at 15s via AbortController so an
      // ALB 504 (which would otherwise hang for 60s) fails fast and retries.
      let stopped = false;
      let consecutiveErrors = 0;
      const maxConsecutiveErrors = 15;

      const poll = async () => {
        if (stopped) return;

        const controller = new AbortController();
        const abortTimer = setTimeout(() => controller.abort(), 15000);

        try {
          const res = await fetch(
            `${backendUrl}/task_progress/${taskId}?sessionId=${encodeURIComponent(sessionId)}`,
            { signal: controller.signal }
          );
          clearTimeout(abortTimer);

          if (!res.ok) {
            // ALB 503/504 without CORS headers arrives here as a failed fetch
            // (browser CORS-blocks it). But some gateways return a non-200 with
            // a JSON body — treat non-ok as retryable regardless.
            throw new Error(`HTTP ${res.status}`);
          }

          const data = await res.json();
          consecutiveErrors = 0;

          setSessionProgress({
            stage: data.stage,
            progress: data.progress,
            message: data.message,
          });

          if (data.stage === 'complete') {
            stopped = true;
            await waitForInferenceReady(sessionId);
          } else if (data.stage === 'error') {
            stopped = true;
            if (onError) {
              // Auto-retry: caller can re-create the session
              onError(data.message);
            } else {
              sessionStorage.removeItem('sessionId');
              sessionStorage.removeItem('ec2Instance');
              setSessionCreated(false);
              setSettingUpResources(false);
              enqueueSnackbar(`Setup failed: ${data.message}`, { variant: 'error' });
            }
          } else if (data.stage === 'not_found') {
            stopped = true;
            sessionStorage.removeItem('sessionId');
            sessionStorage.removeItem('ec2Instance');
            setSessionCreated(false);
            if (onNotFound) {
              // ECS rollover: session vanished on the new container — auto-restart.
              onNotFound();
            } else {
              setSettingUpResources(false);
              enqueueSnackbar('Connection lost. Please refresh to try again.', { variant: 'warning' });
            }
          } else {
            // Still in progress — schedule next poll.
            setTimeout(poll, 2000);
          }
        } catch {
          clearTimeout(abortTimer);
          consecutiveErrors++;
          if (consecutiveErrors >= maxConsecutiveErrors) {
            stopped = true;
            setSettingUpResources(false);
            enqueueSnackbar('Failed to poll session progress', { variant: 'error' });
          } else {
            // Transient 503/504/CORS error — retry after brief delay.
            setTimeout(poll, 2000);
          }
        }
      };

      poll();
    },
    [backendUrl, enqueueSnackbar, waitForInferenceReady]
  );

  const createSessionAndInit = useCallback(
    async (s3Path) => {
      try {
        setSessionCreated(true);
        setSettingUpResources(true);
        setSessionError(null);
        // Only reuse a cached session ID if it was created for the same s3 path.
        // Chrome copies sessionStorage to tabs opened with window.open, so without
        // this check a single-image tab would inherit the parent's session ID and
        // accidentally resume a different experiment's session on a 409.
        // In single-image mode, ALWAYS generate a fresh session ID to avoid
        // colliding with any existing timelapse sesssion on the same folder.
        let sid = sessionIdQuery;
        if (!sid && !singleImageMode) {
          const cachedPath = sessionStorage.getItem('sessionPath');
          if (cachedPath === s3Path) {
            sid = sessionStorage.getItem('sessionId');
          }
        }
        if (!sid) {
          sid = crypto.randomUUID();
        }
        sessionStorage.setItem('sessionId', sid);
        setResolvedSessionId(sid);
        const response = await fetch(`${backendUrl}/create_session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ s3_path: s3Path, sessionId: sid }),
        });
        const result = await response.json();

        if (response.status === 503) {
          // Capacity full. Backend never evicts another active user's session;
          // we wait for a slot to free instead of failing. Poll /capacity (cheap)
          // and re-attempt create_session when a slot opens.
          const detail = result?.detail || {};
          const retryAfter = (detail.retry_after_seconds || 15) * 1000;
          const maxSlots = detail.max_sessions || 2;
          setSessionProgress({
            stage: 'waiting_for_slot',
            progress: 5,
            message: `All ${maxSlots} analysis slots are in use. Waiting for one to free...`,
          });
          enqueueSnackbar(
            `All analysis slots are busy. Your session will start automatically when a slot frees.`,
            { variant: 'info', autoHideDuration: 6000 }
          );
          // Poll until available, then retry. Bounded so we don't spin forever
          // if the cluster never frees up — gives the user a chance to back out.
          const queueDeadline = Date.now() + 5 * 60 * 1000; // 5 min cap
          const waitForSlot = async () => {
            while (Date.now() < queueDeadline) {
              await new Promise((r) => setTimeout(r, retryAfter));
              try {
                const cap = await fetch(`${backendUrl}/capacity`);
                if (cap.ok) {
                  const j = await cap.json();
                  if (j.available) return true;
                  setSessionProgress({
                    stage: 'waiting_for_slot',
                    progress: 5,
                    message: `Waiting for a slot... (${j.active_sessions}/${j.max_sessions} in use)`,
                  });
                }
              } catch { /* transient — keep polling */ }
            }
            return false;
          };
          const slotOpened = await waitForSlot();
          if (slotOpened) {
            // Re-enter with same s3Path; sessionCreated will be reset below.
            setSessionCreated(false);
            createSessionAndInit(s3Path);
          } else {
            setSessionCreated(false);
            setSettingUpResources(false);
            enqueueSnackbar(
              'Still waiting for an analysis slot after 5 minutes. Please try again later.',
              { variant: 'warning', autoHideDuration: 8000 }
            );
          }
          return;
        }

        if (response.status === 409) {
          if (result.current_session_id && result.current_session_id === sid) {
            // Our own session is already running (e.g. page refresh) — safe to resume
            await waitForInferenceReady(result.current_session_id);
          } else if (result.active_tasks?.length && !result.current_session_id) {
            const taskId = result.active_tasks[0];
            sessionStorage.setItem('sessionId', taskId);
            setResolvedSessionId(taskId);
            pollSessionProgress(taskId, taskId);
          } else {
            // A different session is running on this server
            setSessionCreated(false);
            setSettingUpResources(false);
            enqueueSnackbar(
              'Another session is already running on this server. End that session first or wait for it to complete.',
              { variant: 'error', autoHideDuration: 8000 }
            );
            return;
          }
          return;
        }
        if (!response.ok) throw new Error(result.error || 'Failed to create session');

        sessionStorage.setItem('sessionId', result.session_id);
        sessionStorage.setItem('sessionPath', s3Path);
        setResolvedSessionId(result.session_id);
        if (result.task_id) {
          pollSessionProgress(
            result.task_id,
            result.session_id,
            () => {
              // ECS rollover: the backend restarted between session creation and init
              // completing. Re-create the session, but only up to MAX_RECREATES so a
              // tab left open across restarts can't refill GPU slots indefinitely.
              recreateCount.current += 1;
              if (recreateCount.current > MAX_RECREATES) {
                sessionStorage.removeItem('sessionId');
                sessionStorage.removeItem('ec2Instance');
                setSessionCreated(false);
                setSettingUpResources(false);
                enqueueSnackbar(
                  'Lost connection to the analysis server. Please reload the page to reconnect.',
                  { variant: 'warning', autoHideDuration: 8000 }
                );
                return;
              }
              setSessionProgress({
                stage: 'connecting_to_server',
                progress: 10,
                message: 'Backend restarted, reconnecting...',
              });
              initRetryCount.current = 0;
              createSessionAndInit(s3Path);
            },
            (errorMsg) => {
              // Task failed on the backend (timeout, cancellation, etc.)
              // Auto-retry up to MAX_INIT_RETRIES before giving up.
              initRetryCount.current += 1;
              if (initRetryCount.current <= MAX_INIT_RETRIES) {
                console.warn(
                  `Init failed (attempt ${initRetryCount.current}/${MAX_INIT_RETRIES}): ${errorMsg}. Retrying...`
                );
                setSessionProgress({
                  stage: 'connecting_to_server',
                  progress: 10,
                  message: `Retrying setup (attempt ${initRetryCount.current + 1})...`,
                });
                // Generate a fresh session ID so the backend doesn't reject a re-create
                const freshSid = crypto.randomUUID();
                sessionStorage.setItem('sessionId', freshSid);
                setResolvedSessionId(freshSid);
                createSessionAndInit(s3Path);
              } else {
                // Exhausted retries — surface the real reason (e.g. no images)
                // so the page shows it instead of a generic connection error.
                sessionStorage.removeItem('sessionId');
                sessionStorage.removeItem('ec2Instance');
                setSessionCreated(false);
                setSettingUpResources(false);
                setSessionError(errorMsg || 'Analysis setup failed.');
                enqueueSnackbar(
                  `Setup failed after ${MAX_INIT_RETRIES + 1} attempts: ${errorMsg}`,
                  { variant: 'error' }
                );
              }
            }
          );
        } else {
          await waitForInferenceReady(result.session_id);
        }
      } catch (e) {
        console.error('Error in createSessionAndInit:', e);
        setSessionCreated(false);
        setSettingUpResources(false);
        enqueueSnackbar('Failed to connect. Please try again.', { variant: 'error' });
      }
    },
    [backendUrl, sessionIdQuery, singleImageMode, enqueueSnackbar, pollSessionProgress, waitForInferenceReady]
  );

  const checkHealth = useCallback(
    (s3Path) => {
      if (sessionCreatedRef.current) return;
      let tries = 0;
      const maxTries = isNewBackend ? 6 : 15;
      const retryInterval = isNewBackend ? 5000 : 10000;

      const poll = async () => {
        if (++tries > maxTries) {
          setSettingUpResources(false);
          enqueueSnackbar(
            'Failed to connect to server after multiple attempts.',
            { variant: 'error' }
          );
          return;
        }
        setSessionProgress((prev) => ({
          ...prev,
          stage: 'connecting_to_server',
          progress: Math.min(95, (tries / maxTries) * 100),
          message: `Connecting to server (attempt ${tries}/${maxTries})...`,
        }));
        try {
          const sid = sessionIdQuery || sessionStorage.getItem('sessionId');
          const res = await fetch(
            `${backendUrl}/health?sessionId=${encodeURIComponent(sid)}`
          );
          if (res.ok && !sessionCreatedRef.current) {
            const health = await res.json();
            // Show capacity info if server is busy initializing other sessions
            if (health.pending_inits > 0) {
              setSessionProgress((prev) => ({
                ...prev,
                stage: 'connecting_to_server',
                progress: Math.min(95, (tries / maxTries) * 100),
                message: `Server is preparing ${health.pending_inits} other analysis${health.pending_inits > 1 ? 'es' : ''}. Your session will start soon...`,
              }));
            }
            await createSessionAndInit(s3Path);
          } else if (!res.ok) {
            setTimeout(poll, retryInterval);
          }
        } catch {
          setTimeout(poll, retryInterval);
        }
      };
      poll();
    },
    [backendUrl, sessionIdQuery, enqueueSnackbar, createSessionAndInit]
  );

  // ── Readiness check helper ──
  const checkSessionReadiness = useCallback(
    async (sessionId, showMessage = true) => {
      if (!serverReady) {
        if (showMessage)
          enqueueSnackbar(
            'Setup not complete. Please wait a moment.',
            { variant: 'warning' }
          );
        return false;
      }
      try {
        const response = await fetch(
          `${backendUrl}/check_inference_ready/${sessionId}?sessionId=${encodeURIComponent(sessionId)}`
        );
        const result = await response.json();
        if (!result.inference_ready) {
          if (showMessage)
            enqueueSnackbar('Still preparing, please wait...', { variant: 'info' });
          return false;
        }
        return true;
      } catch {
        if (showMessage)
          enqueueSnackbar('Error checking session status.', { variant: 'error' });
        return false;
      }
    },
    [backendUrl, serverReady, enqueueSnackbar]
  );

  // ── Session termination ──
  const handleEndSession = useCallback(async () => {
    // Stop heartbeat immediately
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
    const sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      enqueueSnackbar('Nothing to close.', { variant: 'warning' });
      return;
    }
    setIsEnding(true);
    try {
      if (isNewBackend) {
        // Notify backend to free GPU resources and mark session as ended
        try {
          await fetch(`${backendUrl}/end_session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
          });
        } catch { /* best-effort */ }
        enqueueSnackbar('Analysis ended.', { variant: 'success' });
        window.close();
      } else {
        // Legacy: terminate the EC2 instance
        const response = await fetch(TERMINATE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const result = await response.json();
        if (result.statusCode === 200) {
          enqueueSnackbar('Analysis ended. Closing window...', { variant: 'success' });
          window.close();
        } else {
          throw new Error('Termination API responded with failure.');
        }
      }
    } catch (error) {
      console.error('Error ending session:', error);
      enqueueSnackbar('Failed to close. Please try again.', { variant: 'error' });
    } finally {
      setIsEnding(false);
    }
  }, [enqueueSnackbar, backendUrl]);

  // ── Heartbeat: keep session alive while tab is open ──
  useEffect(() => {
    if (!isNewBackend || !serverReady) return;
    const sid = sessionStorage.getItem('sessionId');
    if (!sid) return;

    // Idle/backgrounded tabs must not hold a slot. With one GPU pod and
    // MAX_SESSIONS=4, a few forgotten-but-open tabs heartbeat forever and
    // saturate everyone (the 60s ping keeps resetting last_accessed, so the
    // server-side 2h10m idle timeout never fires). Only ping while the tab is
    // visible AND the user interacted within IDLE_MS. When pinging stops, the
    // session lapses server-side in ~5 min and becomes LRU-evictable. A session
    // with a running task still holds its slot via the task, not the heartbeat,
    // so in-flight analyses are unaffected. Resumes automatically on return; if
    // the slot was already reclaimed, the next ping returns 'expired' (handled).
    const sendHeartbeat = async () => {
      if (!shouldHeartbeat({
        visibilityState: document.visibilityState,
        lastActivity: lastActivity.current,
        now: Date.now(),
      })) return;
      try {
        const res = await fetch(`${backendUrl}/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: sid }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'expired') {
            // Server evicted our session — clear local state
            console.warn('Session expired on server, clearing local state.');
            sessionStorage.removeItem('sessionId');
            sessionStorage.removeItem('ec2Instance');
            setSessionCreated(false);
            setServerReady(false);
            setSettingUpResources(false);
            enqueueSnackbar(
              'Session expired. Please reload to start a new analysis.',
              { variant: 'warning' }
            );
          }
        }
      } catch {
        // Network blip — ignore, next heartbeat will retry
      }
    };

    // Track user activity so an idle tab stops heartbeating. Returning to the
    // tab (visible) counts as activity and fires one immediate ping so the slot
    // is re-claimed without waiting up to 60s for the next tick.
    const markActivity = () => { lastActivity.current = Date.now(); };
    const onVisible = () => {
      if (document.visibilityState === 'visible') { markActivity(); sendHeartbeat(); }
    };
    const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'scroll'];
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisible);

    heartbeatTimer.current = setInterval(sendHeartbeat, 60000);
    return () => {
      if (heartbeatTimer.current) {
        clearInterval(heartbeatTimer.current);
        heartbeatTimer.current = null;
      }
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActivity));
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [backendUrl, serverReady, enqueueSnackbar]);

  // ── Notify backend on tab/window close (best-effort via sendBeacon) ──
  //
  // Chrome no longer guarantees `beforeunload` runs when a tab is closed,
  // discarded by the OS, or backgrounded for long enough that the tab is
  // killed. Single-image colony-count tabs (opened via window.open from the
  // dashboard) are the worst offenders — users open and close lots of them.
  // We register THREE listeners that together cover every real-world close:
  //   - beforeunload: fires on explicit reload + most navigations
  //   - pagehide:     the only one that reliably fires when the page is
  //                   discarded / put into bfcache / tab is killed
  //   - visibilitychange (hidden): fires when the user backgrounds the tab;
  //                   we use it for single-image mode where lingering > 0
  //                   sessions block other users from getting a slot.
  // Plus a React-unmount teardown for single-image mode — the page component
  // unmounts on route change or window.close(), guaranteeing one last send.
  useEffect(() => {
    if (!isNewBackend || analysisRestoreMode) return;
    let alreadySent = false;
    const sendEnd = (reason) => {
      if (alreadySent) return;
      const sid = sessionStorage.getItem('sessionId');
      if (!sid) return;
      alreadySent = true;
      // NOTE: do NOT use navigator.sendBeacon here. sendBeacon always sends
      // with `credentials: include`, which the browser rejects when the
      // backend responds with `Access-Control-Allow-Origin: *` (our FastAPI
      // CORS default). The end_session call gets blocked client-side and the
      // slot stays held. fetch with `keepalive: true` survives page unload
      // and defaults to `credentials: same-origin`, so cross-origin works
      // with our wildcard ACAO.
      try {
        fetch(`${backendUrl}/end_session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: sid, reason }),
          keepalive: true,
        }).catch(() => { /* unload — nothing we can do */ });
      } catch (_) { /* swallow */ }
    };
    const onBeforeUnload = () => sendEnd('beforeunload');
    const onPageHide = () => sendEnd('pagehide');
    const onVisibility = () => {
      // For single-image mode, treat 'hidden' as a strong signal that the tab
      // is going away (users typically close colony-count tabs as soon as
      // they're done). For timelapse, don't end on background — users tab-
      // switch while analyses run.
      if (singleImageMode && document.visibilityState === 'hidden') {
        sendEnd('visibility-hidden');
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      // React unmount = route change or window.close from owner code. In
      // single-image mode this is the last reliable chance to free the slot
      // — fire it explicitly.
      if (singleImageMode) sendEnd('unmount');
    };
  }, [backendUrl, analysisRestoreMode, singleImageMode]);

  // ── Boot sequence effect ──
  useEffect(() => {
    if (analysisRestoreMode) return;

    // In single-image mode, always clear inherited sessionStorage to prevent
    // reusing the parent tab's session (window.open copies sessionStorage).
    if (singleImageMode) {
      sessionStorage.removeItem('sessionId');
      sessionStorage.removeItem('sessionPath');
      sessionStorage.removeItem('ec2Instance');
    }

    // Restore from sessionStorage if returning to an already-booted instance.
    // Only resume if the cached session was created for the same S3 path —
    // otherwise a new tab (e.g. single-image mode) would inherit the parent
    // tab's session and display the wrong experiment's frames.
    const isSingleFile = /\.(jpe?g|png)$/i.test(initialPath);
    const s3PathForInit = isSingleFile
      ? `s3://${process.env.REACT_APP_USER_BUCKET}/${initialPath}`
      : `s3://${process.env.REACT_APP_USER_BUCKET}/${initialPath}/`;
    const cachedSessionPath = sessionStorage.getItem('sessionPath');
    const pathMatches = !singleImageMode && cachedSessionPath === s3PathForInit;
    const ec2Details = sessionStorage.getItem('ec2Instance');
    if (ec2Details && pathMatches) {
      try {
        const details = JSON.parse(ec2Details);
        if (details.publicIp && details.instanceId) {
          const sid = sessionIdQuery || sessionStorage.getItem('sessionId');
          if (sid) {
            // eslint-disable-next-line no-inner-declarations
            async function resume() {
              try {
                const res = await fetch(
                  `${backendUrl}/task_progress/${sid}?sessionId=${encodeURIComponent(sid)}`
                );
                if (res.ok) {
                  const task = await res.json();
                  if (task.stage && task.stage !== 'not_found' && task.stage !== 'complete') {
                    setResolvedSessionId(sid);
                    pollSessionProgress(sid, sid);
                    return;
                  }
                  if (task.stage === 'complete') {
                    setResolvedSessionId(sid);
                    await waitForInferenceReady(sid);
                    return;
                  }
                }
              } catch { /* fall through */ }
              // Session or task not found — stale after ECS rollover.
              // Clear stored state and fall through to create a new session.
              sessionStorage.removeItem('sessionId');
              sessionStorage.removeItem('ec2Instance');
              setSessionCreated(false);
              healthCheckStarted.current = false;
              setSessionProgress({
                stage: 'connecting_to_server',
                progress: 10,
                message: 'Previous session expired, creating new session...',
              });
              checkHealth(s3PathForInit);
            }
            resume();
            return;
          }
        }
      } catch { /* fall through */ }
    }

    // No existing session (or path mismatch) → boot sequence
    if (!sessionCreated && !healthCheckStarted.current) {
      healthCheckStarted.current = true;
      // Clear stale session data if path doesn't match (e.g. new single-image tab)
      if (!pathMatches) {
        sessionStorage.removeItem('sessionId');
        sessionStorage.removeItem('sessionPath');
      }

      if (isNewBackend) {
        // New backend: no 45s boot wait. Shared service is already running
        // or starts in ~10-15s via warm pool.
        setSessionProgress({
          stage: 'connecting_to_server',
          progress: 10,
          message: 'Connecting to analysis server...',
        });
        checkHealth(s3PathForInit);
      } else {
        // Legacy: 45s EC2 boot wait
        let countdown = 45;
        const total = 45;
        const ci = setInterval(() => {
          countdown--;
          setSessionProgress({
            stage: 'waiting_for_instance',
            progress: ((total - countdown) / total) * 10,
            message: `Preparing server... (${countdown}s remaining)`,
          });
          if (countdown <= 0) clearInterval(ci);
        }, 1000);

        setTimeout(() => {
          clearInterval(ci);
          setSessionProgress((prev) => ({
            stage: 'connecting_to_server',
            progress: Math.max(prev.progress, 10),
            message: 'Starting connection to server...',
          }));
          checkHealth(s3PathForInit);
        }, 45000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisRestoreMode]);

  // Timeout failsafe
  useEffect(() => {
    const timeoutMs = isNewBackend ? 180000 : 120000;
    const timeout = setTimeout(() => {
      if (settingUpResources) {
        setSettingUpResources(false);
        if (!serverReady) {
          enqueueSnackbar(
            'Session setup took longer than expected. You may proceed but some features might not work.',
            { variant: 'warning' }
          );
        }
      }
    }, timeoutMs);
    return () => clearTimeout(timeout);
  }, [settingUpResources, serverReady, enqueueSnackbar]);

  return {
    settingUpResources,
    serverReady,
    sessionProgress,
    sessionError,
    isEnding,
    checkSessionReadiness,
    handleEndSession,
    backendUrl,
    resolvedSessionId,
  };
}
